import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from '../base/Logo';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fermer le menu mobile en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Nos Produits', path: '/nos-produits' },
    { name: 'Nos Services', path: '/nos-services' },
  ];

  // Forcer le fond blanc sur la page d'étude et mentions légales
  const isStudyPage = location.pathname === '/etude-gratuite';
  const isLegalPage = location.pathname === '/mentions-legales';
  const isPrivacyPage = location.pathname === '/politique-de-confidentialite';
  const isCookiesPage = location.pathname === '/politique-de-cookies';
  const shouldShowBackground = scrolled || isStudyPage || isLegalPage || isPrivacyPage || isCookiesPage;

  return (
    <motion.nav
      ref={mobileMenuRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        shouldShowBackground
          ? 'bg-white/98 backdrop-blur-xl shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer group">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <Logo inverted={!shouldShowBackground} />
            </motion.div>
          </Link>

          {/* Navigation Links + CTA */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 lg:px-6 py-2 lg:py-3 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-emerald-600'
                    : shouldShowBackground
                    ? 'text-slate-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-400'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className={`absolute inset-0 rounded-full -z-10 ${
                      shouldShowBackground ? 'bg-emerald-100' : 'bg-white/20 backdrop-blur-sm'
                    }`}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </Link>
            ))}
            
            {/* Bouton Étude gratuite */}
            {!isStudyPage && (
              <Link
                to="/etude-gratuite"
                className="ml-2 lg:ml-4 px-4 lg:px-6 py-2 lg:py-3 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white rounded-full text-xs sm:text-sm font-bold shadow-lg hover:shadow-emerald-500/50 transition-all cursor-pointer whitespace-nowrap hover:scale-105"
              >
                Étude gratuite
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full transition-all ${
            shouldShowBackground ? 'text-slate-900 hover:bg-slate-100' : 'text-white hover:bg-white/10'
          } cursor-pointer`}>
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pb-3 bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-lg"
          >
            <div className="flex flex-col gap-1.5 pt-3 px-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-3 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    location.pathname === link.path
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {!isStudyPage && (
                <Link
                  to="/etude-gratuite"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mt-1.5 px-3 py-2.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white rounded-lg text-xs font-bold text-center cursor-pointer"
                >
                  Étude gratuite
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}
